// NASA SUPIN license-gate configuration.
//
// SUPIN is released under a NASA Software Usage Agreement with usage
// restrictions (internal/domestic use only, no redistribution). The prompt
// landing page surfaces those restrictions and gates the Generate button behind
// an access password whenever a SUPIN inlet is selected.
//
// "Vault" note: a password checked in the browser can never be a real secret —
// whatever gates a client-side button ships to the client. So we do the next
// best thing and keep the *plaintext* out of source: only the SHA-256 digest is
// stored here, and the entered value is hashed with Web Crypto and compared. An
// optional VITE_SUPIN_LICENSE_SHA256 build-time override lets the digest live in
// env/CI rather than the repo if desired. This is access friction + an audit
// acknowledgement, not cryptographic protection of the SUPIN binary (that lives
// server-side behind the authenticated runner — see src/config/supin.ts).

// SHA-256 hex digest of the access password. Override at build time with
// VITE_SUPIN_LICENSE_SHA256 (also hex). Default is the digest of the shared
// SUPIN access password.
export const SUPIN_LICENSE_SHA256: string =
  import.meta.env.VITE_SUPIN_LICENSE_SHA256 ??
  'bed0e6613fd45cc8953c1f4d8073bc1297e0eb86b68f7809356db0108fd71866';

/** Hex-encode a byte buffer (lowercase). */
function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * True when `input` hashes to the configured SUPIN access digest. Uses Web
 * Crypto's SHA-256 so the plaintext never appears in the bundle. Async because
 * `crypto.subtle.digest` is; resolves false (never throws) on any error.
 */
export async function verifySupinLicensePassword(
  input: string,
): Promise<boolean> {
  try {
    const bytes = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return toHex(digest) === SUPIN_LICENSE_SHA256.toLowerCase();
  } catch {
    return false;
  }
}

// The signed NASA Software Usage Agreement, page by page (public/supin-license).
// Shown one at a time in the manual slideshow on the right of the license gate.
export const SUPIN_LICENSE_PAGES: string[] = [
  '/supin-license/page-1.png',
  '/supin-license/page-2.png',
  '/supin-license/page-3.png',
  '/supin-license/page-4.png',
  '/supin-license/page-5.png',
  '/supin-license/page-6.png',
  '/supin-license/page-7.png',
];
